import {WBSlateElementType} from '../../components/Slate/WBSlate/types';
import {
  ProjectInternalIdFromNameDocument,
  ProjectInternalIdFromNameQuery,
} from '../../generated/graphql';
import {ApolloClient} from '../../state/types';
import {SlateReport} from './types';

/**
 * Migrates run sets in a report spec to use correct project IDs.
 * For each run set in the report:
 * - If run set has a project ID, ensure it is in the correct format (base64-encoded project internal ID).
 * - If run set has project entity/name but no ID, look up the project internal ID and attach it.
 * The spec object is updated immutably. Original spec is returned if no updates are needed.
 */
export async function migrateReportRunSetProjects(
  spec: SlateReport,
  apolloClient: ApolloClient
): Promise<SlateReport> {
  // track if we need to update the spec; if not, we can return the original spec
  let shouldUpdateSpec = false;

  // iterate over blocks to find panel grids with run sets that need to be migrated
  const updatedBlocks = await Promise.all(
    spec.blocks.map(async function migrateBlock(block: WBSlateElementType) {
      // some blocks, like heading elements, may have collapsed children.
      // we need to recusively check them for nested panel grids to migrate.
      if (block.collapsedChildren) {
        return {
          ...block,
          collapsedChildren: await Promise.all(
            block.collapsedChildren.map(migrateBlock)
          ),
        };
      }

      if (block.type !== 'panel-grid' || !block.metadata.runSets) {
        return block;
      }

      const updatedRunSets = await Promise.all(
        block.metadata.runSets.map(async runSet => {
          // no migration needed if run set doesn't have explicit project configured.
          // run set will point to current project by parsing entity/project names from URL.
          if (!runSet.project) {
            return runSet;
          }

          let newProjectId: string | undefined;
          if (runSet.project.id) {
            // project ID is configured; it may be a legacy ID that needs to be converted
            const convertedId = getProjectInternalId(runSet.project.id);
            if (convertedId !== runSet.project.id) {
              newProjectId = convertedId;
            }
          } else {
            // project ID is not configured; we need to look it up by entity/project names
            try {
              const queryResult =
                await apolloClient.query<ProjectInternalIdFromNameQuery>({
                  query: ProjectInternalIdFromNameDocument,
                  variables: {
                    entityName: runSet.project.entityName,
                    projectName: runSet.project.name,
                  },
                });
              newProjectId = queryResult.data.project?.internalId;
              if (!newProjectId) {
                throw new Error('Project not found');
              }
            } catch (error) {
              console.error('Error looking up run set project ID', error);
            }
          }

          if (!newProjectId) {
            return runSet;
          }

          shouldUpdateSpec = true;
          return {
            ...runSet,
            project: {
              ...runSet.project,
              id: newProjectId,
            },
          };
        })
      );

      if (!shouldUpdateSpec) {
        return block;
      }

      return {
        ...block,
        metadata: {
          ...block.metadata,
          runSets: updatedRunSets,
        },
      };
    })
  );

  if (!shouldUpdateSpec) {
    return spec;
  }

  return {
    ...spec,
    blocks: updatedBlocks,
  };
}

/** Given a string, return it as a base64-encoded project internal ID */
function getProjectInternalId(projectId: string) {
  try {
    // if it's already an encoded internal ID, return as-is
    if (atob(projectId).startsWith('ProjectInternalId:')) {
      return projectId;
    }
  } catch (err) {
    // it's fine if atob fails; just means we need to encode the ID
  }

  return btoa('ProjectInternalId:' + projectId);
}
